'use client';
/**
 * L'ARBRE DE DÉCISION, RENDU.
 *
 * RQ, 25/08/2026 : « je veux l'arbre dans l'application, et je veux pouvoir le
 * modifier dans la partie lignes de texte et le diagramme se corrige ».
 *
 * ── Ce que ce composant ne fait PAS ────────────────────────────────────────
 *
 * ⚠️ IL NE CALCULE RIEN. L'arbre vient du moteur (`arbreDe`), qui l'obtient en
 * énumérant les combinaisons et en demandant à `interpreter` ce qu'il conclut
 * sur chacune. Refaire ce calcul ici en relisant les règles produirait un
 * second avis — et deux avis sur la même question finissent par diverger, ce
 * qui est exactement le défaut qu'un arbre généré doit supprimer.
 *
 * ── Pourquoi du CSS et non un SVG ─────────────────────────────────────────
 *
 * Un arbre en SVG demande de calculer des positions : largeurs de sous-arbres,
 * évitement des croisements, mise à l'échelle. C'est du travail, et surtout
 * cela casse dès qu'un libellé s'allonge — or les conclusions sont des phrases
 * («Infection ancienne guérie — immunité naturelle»). En CSS, le navigateur
 * fait la mise en page, le texte se replie tout seul et l'impression suit.
 *
 * ⚠️ LES TROUS ET LES AMBIGUÏTÉS SE VOIENT. Un arbre qui ne montrerait que les
 * conclusions se lirait comme complet même troué — c'est précisément ce qu'un
 * arbre doit rendre visible, et ce qu'un tableau de couverture dit en chiffres
 * sans le faire voir.
 */
import React from 'react';
import { card } from '@/components/ui';

export type Feuille =
  | { type: 'regle'; code: string; libelle: string }
  | { type: 'aucune' }
  | { type: 'ambigu'; codes: string[] };

export type Noeud =
  | { type: 'feuille'; feuille: Feuille }
  | { type: 'question'; parametre: string; branches: { libelle: string; enfant: Noeud }[] };

const TRAIT = '1px solid var(--border-strong)';

/** Le ton d'une feuille. La couleur porte l'information clinique, pas la
 *  décoration : ambre = ce qui ne se conclut pas, et cela doit sauter aux yeux. */
function tonFeuille(f: Feuille): { fond: string; bord: string; texte: string } {
  if (f.type === 'aucune') {
    return { fond: 'var(--red-50)', bord: 'var(--red-500)', texte: 'var(--red-600)' };
  }
  if (f.type === 'ambigu') {
    return { fond: 'var(--amber-50)', bord: 'var(--amber-500)', texte: 'var(--amber-600)' };
  }
  return { fond: 'var(--surface-raised)', bord: 'var(--border-default)', texte: 'var(--text-strong)' };
}

function Feuille({ f }: { f: Feuille }) {
  const t = tonFeuille(f);
  return (
    <div style={{
      background: t.fond, border: `1px solid ${t.bord}`, borderRadius: 'var(--radius-md)',
      padding: '7px 11px', fontSize: 12.5, lineHeight: 1.45, color: t.texte,
      maxWidth: 300, minWidth: 150,
    }}>
      {f.type === 'regle' && (
        <>
          <div style={{ fontWeight: 600 }}>{f.libelle}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-muted)' }}>{f.code}</div>
        </>
      )}
      {/* ⚠️ Un trou ne se tait pas. C'est une combinaison que les règles ne
          couvrent pas : le biologiste n'aura aucun commentaire proposé, et il
          vaut mieux le savoir en écrivant la règle qu'à la validation. */}
      {f.type === 'aucune' && (
        <div style={{ fontWeight: 600 }}>
          Aucune conclusion
          <div style={{ fontWeight: 400, fontSize: 12.5, marginTop: 2 }}>
            Ce profil n’est couvert par aucune règle.
          </div>
        </div>
      )}
      {/* Deux règles qui concluent ensemble sont une faute de spécification :
          le moteur REFUSE de choisir, et l'arbre nomme les coupables. */}
      {f.type === 'ambigu' && (
        <div style={{ fontWeight: 600 }}>
          Deux règles concluent
          <div style={{ fontWeight: 400, fontSize: 12.5, marginTop: 2, fontFamily: 'var(--font-mono)' }}>
            {f.codes.join(' · ')}
          </div>
        </div>
      )}
    </div>
  );
}

function Branche({ n, profondeur = 0 }: { n: Noeud; profondeur?: number }) {
  if (n.type === 'feuille') return <Feuille f={n.feuille} />;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{
        alignSelf: 'center', flex: '0 0 auto',
        background: 'var(--surface-sunken)', border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-pill)', padding: '5px 12px',
        fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700,
        color: 'var(--text-strong)', whiteSpace: 'nowrap',
      }}>{n.parametre}</div>

      {/* Les branches, empilées. Le trait vertical relie la question à ses
          réponses : c'est lui qui fait lire l'ensemble comme un arbre plutôt
          que comme une liste indentée. */}
      <div style={{ display: 'grid', gap: 8, borderInlineStart: TRAIT, paddingInlineStart: 14 }}>
        {n.branches.map((b) => (
          <div key={b.libelle} style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
            <span aria-hidden="true" style={{
              position: 'absolute', insetInlineStart: -14, top: '50%',
              width: 14, borderTop: TRAIT,
            }} />
            <span style={{
              flex: '0 0 auto', fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)',
              minWidth: 58, whiteSpace: 'nowrap',
            }}>{b.libelle}</span>
            <Branche n={b.enfant} profondeur={profondeur + 1} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ArbreDecision({ arbre, motif, titre }: {
  arbre: Noeud | null;
  /** Pourquoi il n'y a pas d'arbre. `null` n'est pas une panne. */
  motif?: string | null;
  titre?: string;
}) {
  if (!arbre) {
    return (
      <div style={{ ...card, padding: 14, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
        {motif || 'Aucun arbre : ce jeu n’interroge aucun paramètre.'}
      </div>
    );
  }
  return (
    <div style={{ ...card, padding: 16, overflowX: 'auto' }}>
      {titre && (
        <div style={{
          fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 10,
        }}>{titre}</div>
      )}
      <Branche n={arbre} />
      <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '86ch' }}>
        Cet arbre est <strong>calculé depuis les règles</strong>, jamais dessiné : chaque branche est le verdict
        que le moteur rend réellement sur cette combinaison. Une branche qui s’arrête tôt le fait parce que les
        paramètres restants ne changeraient rien à la conclusion.
      </p>
    </div>
  );
}
